// # packages
import * as React from 'react';
import { boundMethod } from 'autobind-decorator';
import * as _ from 'lodash';

export interface iProps<iItem> {
    /**
     * Class name applied to the container.
     */
    className?: string;
    /**
     * Items that will populate the list.
     */
    items: iItem[];
    /**
     * Items will be grouped into sections by this key, if provided.
     */
    groupBy?: keyof iItem;
    /**
     * Called when a list item is clicked.
     * Note: list items are only clickable when this prop is provided.
     */
    onSelect?: (item: iItem) => any;
    /**
     * Custom render function for group headers.
     */
    renderGroupHeader?: (group: iGroup<iItem>, onGroupHeaderClick: (feature: string)=>void) => React.ReactNode;
    /**
     * Custom render function for list items.
     */
    renderItem?: (item: iItem, clickHandler?: () => any) => React.ReactElement;
    /**
     * Custom renderer for the content when there are no items in the list.
     */
    renderEmptyPlaceholder?: () => React.ReactNode;

    onGroupHeaderClick?: (feature: string)=>void
}

interface iGroup<iItem> {
    title: string;
    items: Array<iItem>;
}

interface iState<iItem> {
    groups: Array<iGroup<iItem>>;
}

/**
 * Renders a list of items.
 * Optionally grouped and clickable.
 */
export class List<iItem> extends React.Component<iProps<iItem>, iState<iItem>> {
    /**
     * The title used for the single group created when no "groupBy" prop is provided.
     *
     * @type {string}
     */
    static ungroupedGroupTitle = 'Ungrouped';

    static defaultProps = {
        onGroupHeaderClick: (feature: string)=>{},
        renderGroupHeader: (group: iGroup<any>, onGroupHeaderClick: (feature: string)=>void) => {
            // don't render the header if this is the default "ungrouped" group
            if (group.title === List.ungroupedGroupTitle) {
                return null;
            }

            return (
                <div className="list__group-header">
                    <h4 className="list__group-title is-interactive" onClick={()=>onGroupHeaderClick(group.title)}>
                        {_.capitalize(_.get(group, 'title', '[Group Title]'))} ({group.items.length.toString()})
                    </h4>
                </div>
            );
        },
        renderItem: (item, clickHandler) => {
            const classNames = [
                'list__item',
                clickHandler ? 'is-interactive' : '',
            ];

            return (
                <div className={classNames.join(' ')} onClick={clickHandler}>
                    {_.get(item, 'label', '[Item has no "label" property]')}
                </div>
            );
        },
        renderEmptyPlaceholder: () => (
          ''
        ),
    };

    /**
     * Rebuilds the state whenever the props change.
     *
     * @param {iProps<iItem>} props
     * @returns {iState<iItem>}
     */
    static getDerivedStateFromProps(props: iProps<any>): iState<any> {
        if (!props.groupBy) {
            return {
                groups: [{
                    title: List.ungroupedGroupTitle,
                    items: props.items,
                }],
            };
        }

        // create object like: Record<groupName, Array<iItem>>
        const groupsByKey = _.groupBy(props.items, props.groupBy);
        // create array like: Array<{ title: key, items: Array<iItem> }>
        const unorderedState = Object.keys(groupsByKey).map(key => ({
            title: key,
            items: groupsByKey[key],
        }));
        // order alphabetically
        return {
            groups: _.sortBy(unorderedState, 'title'),
        };
    }

    public state = {
        groups: [],
    };

    /**
     * Renders the content.
     *
     * @returns {React.ReactNode}
     */
    public render(): React.ReactNode {
        const { className, renderEmptyPlaceholder } = this.props;
        const { groups } = this.state;

        return (
            <article className={`list ${className}`}>
                {groups.map(this.renderGroup)}
                {!groups.length || !groups[0].items.length ? renderEmptyPlaceholder() : null}
            </article>
        );
    }

    /**
     * Makes a click handler for an item that calls the "onSelect" prop if it exists.
     * Null is returned f it doesn't exist.
     *
     * @param {iItem} item
     * @returns {null | (item: iItem) => any}
     */
    @boundMethod
    protected makeClickHandler(item: iItem): null | (() => any) {
        const { onSelect } = this.props;
        if (!onSelect) { return; }

        return () => { onSelect(item); };
    }

    /**
     * Renders a group of items
     *
     * @param {iGroup<iItem>} group
     * @param {number} index
     * @returns {React.ReactNode}
     */
    @boundMethod
    protected renderGroup(group: iGroup<iItem>, index: number): React.ReactNode {
        return (
            <section className="list__group" key={`${index}.${group.title}`}>
                {this.props.renderGroupHeader(group, this.props.onGroupHeaderClick)}
                {group.items.map(this.renderItem)}
            </section>
        );
    }

    /**
     * Renders a single item
     *
     * @param {iItem} group
     * @returns {React.ReactNode}
     */
    @boundMethod
    protected renderItem(item: iItem): React.ReactNode {
        return this.props.renderItem(item, this.makeClickHandler(item));
    }
}

export default List;
